<template>
  <div data-component="Lot/ViewPreChiffrage" class="vpc-container">
    <div v-if="loading" class="vpc-loading">
      <div class="spinner-small"></div>
      <p>Chargement du pré-chiffrage...</p>
    </div>
    <div v-else-if="!preChiffrage" class="vpc-not-found">
      <i class="fas fa-exclamation-triangle vpc-not-found__icon"></i>
      <p>Pré-chiffrage introuvable</p>
      <router-link :to="`/dossier/${propertyId}/timeline`" class="vpc-back-btn">
        <i class="fas fa-arrow-left mr-2"></i>Retour à l'historique
      </router-link>
    </div>
    <div v-else>
      <header class="vpc-header">
        <div class="vpc-header__left">
          <router-link :to="`/dossier/${propertyId}/timeline`" class="vpc-back-link">
            <i class="fas fa-arrow-left mr-1"></i>Historique
          </router-link>
          <h2 class="vpc-header__title">
            Fiche pré-chiffrage
            <span class="vpc-type-badge" :class="preChiffrage.isGpr ? 'vpc-type-badge--gpr' : 'vpc-type-badge--cat'">
              <i :class="preChiffrage.isGpr ? 'fas fa-bolt' : 'fas fa-list-ul'" class="mr-1"></i>
              {{ preChiffrage.isGpr ? 'GPR — EDF' : 'CAT' }}
            </span>
          </h2>
        </div>
        <div class="vpc-header__meta">
          <span class="vpc-meta-item"><i class="fas fa-calendar mr-1"></i>{{ formatDate(preChiffrage.createdAt) }}</span>
          <span v-if="preChiffrage.author" class="vpc-meta-item"><i class="fas fa-user mr-1"></i>{{ preChiffrage.author }}</span>
        </div>
        <button class="vpc-pdf-btn" :disabled="generatingPDF" @click="downloadPDF">
          <i v-if="generatingPDF" class="fas fa-spinner fa-spin mr-1"></i>
          <i v-else class="fas fa-file-pdf mr-1"></i>
          {{ generatingPDF ? 'Génération...' : 'Télécharger PDF' }}
        </button>
      </header>
      <hr class="vpc-divider" />
      <div class="vpc-status-bar">
        <span v-if="preChiffrage.chiffrageTermine === 'Oui'" class="vpc-badge vpc-badge--success"><i class="fas fa-check mr-1"></i>Terminé</span>
        <span v-if="preChiffrage.isSinistre === 'Oui'" class="vpc-badge vpc-badge--warning"><i class="fas fa-exclamation-triangle mr-1"></i>Sinistre</span>
        <span v-if="preChiffrage.photosEnregistrees === 'Oui'" class="vpc-badge vpc-badge--info"><i class="fas fa-camera mr-1"></i>Photos</span>
        <span v-if="preChiffrage.isGpr && preChiffrage.travauxGPRValidesEDF" class="vpc-badge"
          :class="preChiffrage.travauxGPRValidesEDF === 'OUI' ? 'vpc-badge--edf-ok' : 'vpc-badge--edf-ko'"
        ><i class="fas fa-bolt mr-1"></i>EDF : {{ preChiffrage.travauxGPRValidesEDF }}</span>
      </div>

      <!-- GPR summary -->
      <div v-if="preChiffrage.isGpr" class="vpc-section vpc-section--gpr">
        <h3 class="vpc-section__title"><span class="vpc-section__badge vpc-section__badge--gpr">GPR</span> Récapitulatif GPR (EDF)</h3>
        <div class="vpc-summary-grid">
          <div v-if="preChiffrage.coutTotalGPR" class="vpc-summary-item">
            <span class="vpc-summary-item__label">Coût total</span>
            <span class="vpc-summary-item__value">{{ preChiffrage.coutTotalGPR }} €</span>
          </div>
          <div v-if="preChiffrage.dureePrevTravauxGPR" class="vpc-summary-item">
            <span class="vpc-summary-item__label">Durée prévisionnelle</span>
            <span class="vpc-summary-item__value">{{ preChiffrage.dureePrevTravauxGPR }} jours</span>
          </div>
          <div v-if="preChiffrage.coutTravauxGPR" class="vpc-summary-item">
            <span class="vpc-summary-item__label">Coût travaux</span>
            <span class="vpc-summary-item__value">{{ preChiffrage.coutTravauxGPR }} €</span>
          </div>
          <div v-if="preChiffrage.coutDAATGPR" class="vpc-summary-item">
            <span class="vpc-summary-item__label">Coût DAAT</span>
            <span class="vpc-summary-item__value">{{ preChiffrage.coutDAATGPR }} €</span>
          </div>
          <div v-if="preChiffrage.coutDiagElecGPR" class="vpc-summary-item">
            <span class="vpc-summary-item__label">Coût Diag Élec</span>
            <span class="vpc-summary-item__value">{{ preChiffrage.coutDiagElecGPR }} €</span>
          </div>
        </div>
      </div>

      <!-- Lignes CAT -->
      <div v-if="!preChiffrage.isGpr && preChiffrage.lines && preChiffrage.lines.length > 0" class="vpc-section vpc-section--cat">
        <h3 class="vpc-section__title"><span class="vpc-section__badge vpc-section__badge--cat">CAT</span> Lignes ({{ preChiffrage.lines.length }})</h3>
        <div class="vpc-lines">
          <div v-for="(line, idx) in preChiffrage.lines" :key="idx" class="vpc-line-card vpc-line-card--cat">
            <div class="vpc-line-card__num">{{ idx + 1 }}</div>
            <div class="vpc-line-card__body">
              <div class="vpc-line-card__chips">
                <span class="vpc-chip vpc-chip--room"><i class="fas fa-door-open mr-1"></i>{{ line.roomType || '—' }}</span>
                <span v-if="line.workType" class="vpc-chip vpc-chip--work"><i class="fas fa-tools mr-1"></i>{{ line.workType }}</span>
                <span v-if="line.sinistre === 'oui'" class="vpc-chip vpc-chip--sinistre"><i class="fas fa-exclamation-triangle mr-1"></i>Sinistre</span>
                <span v-if="line.montantDiag != null" class="vpc-chip"><i class="fas fa-euro-sign mr-1"></i>{{ line.montantDiag }} €</span>
                <span v-if="line.dureeDiag != null" class="vpc-chip"><i class="fas fa-clock mr-1"></i>{{ line.dureeDiag }} j</span>
              </div>
              <div v-if="line.workFields && Object.keys(line.workFields).length > 0" class="vpc-line-card__fields">
                <span v-for="(val, key) in line.workFields" :key="key" class="vpc-field-pill">
                  <span class="vpc-field-pill__key">{{ key }}</span><span class="vpc-field-pill__val">{{ val }}</span>
                </span>
              </div>
              <div v-if="line.commentairesSinistre" class="vpc-line-card__comment"><i class="fas fa-comment-alt mr-1"></i>{{ line.commentairesSinistre }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Lignes GPR -->
      <div v-if="preChiffrage.isGpr && preChiffrage.lines && preChiffrage.lines.length > 0" class="vpc-section vpc-section--gpr">
        <h3 class="vpc-section__title"><span class="vpc-section__badge vpc-section__badge--gpr">GPR</span> Lignes GPR ({{ preChiffrage.lines.length }})</h3>
        <div class="vpc-lines">
          <div v-for="(line, idx) in preChiffrage.lines" :key="idx" class="vpc-line-card vpc-line-card--gpr">
            <div class="vpc-line-card__num vpc-line-card__num--gpr">{{ idx + 1 }}</div>
            <div class="vpc-line-card__body">
              <div class="vpc-line-card__chips">
                <span v-if="line.precoTravauxGPR" class="vpc-chip"><i class="fas fa-hard-hat mr-1"></i>{{ line.precoTravauxGPR }}</span>
                <span v-if="line.coutTravauxGPR != null" class="vpc-chip"><i class="fas fa-euro-sign mr-1"></i>Trav. : {{ line.coutTravauxGPR }} €</span>
                <span v-if="line.coutDAATGPR != null" class="vpc-chip"><i class="fas fa-euro-sign mr-1"></i>DAAT : {{ line.coutDAATGPR }} €</span>
                <span v-if="line.coutDiagElecGPR != null" class="vpc-chip"><i class="fas fa-euro-sign mr-1"></i>Élec : {{ line.coutDiagElecGPR }} €</span>
                <span v-if="line.dureePrevTravauxGPR != null" class="vpc-chip"><i class="fas fa-clock mr-1"></i>{{ line.dureePrevTravauxGPR }} j</span>
                <span v-if="line.travauxGPRValidesEDF" class="vpc-chip"
                  :class="line.travauxGPRValidesEDF === 'OUI' ? 'vpc-chip--edf-ok' : 'vpc-chip--edf-ko'"
                ><i class="fas fa-bolt mr-1"></i>EDF : {{ line.travauxGPRValidesEDF }}</span>
              </div>
              <div v-if="line.commentairesSinistre" class="vpc-line-card__comment"><i class="fas fa-comment-alt mr-1"></i>{{ line.commentairesSinistre }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Commentaires généraux -->
      <div v-if="preChiffrage.commentairesGeneraux" class="vpc-section">
        <h3 class="vpc-section__title"><span class="vpc-section__badge"><i class="fas fa-info-circle"></i></span> Informations complémentaires</h3>
        <div class="vpc-comment-block">
          <strong>Commentaires généraux :</strong><p>{{ preChiffrage.commentairesGeneraux }}</p>
        </div>
      </div>

      <div class="vpc-readonly-banner">
        <i class="fas fa-lock mr-2"></i>
        Cette fiche est en lecture seule. Pour modifier le pré-chiffrage, utilisez la page Pré-chiffrages.
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { generatePreChiffragePDF } from '../../utils/generatePreChiffragePDF'
export default {
  name: 'ViewPreChiffrage',
  data() { return { loading: true, preChiffrage: null, property: null, generatingPDF: false } },
  computed: {
    propertyId() { return this.$route.params.id },
    preChiffrageId() { return this.$route.params.preChiffrageId }
  },
  methods: {
    formatDate(date) {
      if (!date) return ''
      return moment(date).format('DD/MM/YYYY à HH:mm')
    },
    async loadPreChiffrage() {
      try {
        const dexieService = (await import('../../services/dexieService')).default
        const property = await dexieService.getProperty(this.propertyId)
        this.property = property
        if (!property || !property.preChiffrages) { this.preChiffrage = null; return }
        const pc = property.preChiffrages.find(p => String(p.id) === String(this.preChiffrageId))
        if (!pc) { this.preChiffrage = null; return }
        this.preChiffrage = {
          id: pc.id, createdAt: pc.createdAt, updatedAt: pc.updatedAt,
          author: pc.creator?.fullName || pc.creator?.email || null,
          isGpr: pc.isGpr || false, lines: pc.lines || [],
          precoTravauxGPR: pc.precoTravauxGPR,
          dureePrevTravauxGPR: pc.dureePrevTravauxGPR,
          coutTravauxGPR: pc.coutTravauxGPR, coutDAATGPR: pc.coutDAATGPR,
          coutDiagElecGPR: pc.coutDiagElecGPR, coutTotalGPR: pc.coutTotalGPR,
          travauxGPRValidesEDF: pc.travauxGPRValidesEDF,
          isSinistre: pc.isSinistre, commentairesSinistre: pc.commentairesSinistre,
          commentairesGeneraux: pc.commentairesGeneraux,
          chiffrageTermine: pc.chiffrageTermine, photosEnregistrees: pc.photosEnregistrees
        }
      } catch (e) { console.error('Erreur chargement pré-chiffrage:', e); this.preChiffrage = null }
      finally { this.loading = false }
    },
    downloadPDF() {
      this.generatingPDF = true
      try {
        generatePreChiffragePDF(this.preChiffrage, this.property)
      } finally {
        setTimeout(() => { this.generatingPDF = false }, 600)
      }
    }
  },
  async mounted() { await this.loadPreChiffrage() }
}
</script>

<style scoped lang="scss">
@import '@/assets/styles/variables.scss';
$cat-color: #68CFDF; $cat-dark: #56B1BF; $cat-light: rgba(104,207,223,.12);
$gpr-color: #2563eb; $gpr-mid: #3b82f6;  $gpr-light: #eff6ff;

.vpc-container { padding: 1.5rem 2rem; max-width: 900px; margin: 0 auto; }
.vpc-loading { display: flex; flex-direction: column; align-items: center; gap: 1rem; padding: 3rem; color: #64748b; }
.vpc-not-found { display: flex; flex-direction: column; align-items: center; gap: 1rem; padding: 3rem; color: #94a3b8; text-align: center;
  &__icon { font-size: 2.5rem; }
}
.vpc-back-btn { display: inline-flex; align-items: center; padding: 0.5rem 1.2rem; background: $cat-dark; color: #fff; border-radius: 6px; text-decoration: none; font-weight: 600; &:hover { opacity: 0.85; } }
.vpc-header {
  display: flex; align-items: flex-start; justify-content: space-between; flex-wrap: wrap; gap: 0.75rem; margin-bottom: 0.5rem;
  &__left { display: flex; flex-direction: column; gap: 0.4rem; }
  &__title { font-size: 1.4rem; font-weight: 700; color: #1e293b; display: flex; align-items: center; gap: 0.6rem; }
  &__meta { display: flex; gap: 1rem; flex-wrap: wrap; align-items: center; }
}
.vpc-back-link { font-size: 0.85rem; color: $cat-dark; text-decoration: none; &:hover { text-decoration: underline; } }
.vpc-pdf-btn {
  display: inline-flex; align-items: center; gap: 0.4rem;
  padding: 0.45rem 1rem; border-radius: 6px; border: none; cursor: pointer;
  font-size: 0.85rem; font-weight: 600; color: #fff;
  background: linear-gradient(135deg, #e74c3c, #c0392b);
  transition: opacity 0.15s;
  &:hover:not(:disabled) { opacity: 0.85; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
}
.vpc-meta-item { font-size: 0.85rem; color: #64748b; }
.vpc-divider { border: none; border-top: 1px solid #e2e8f0; margin: 0.75rem 0 1rem; }
.vpc-status-bar { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-bottom: 1.5rem; }
.vpc-type-badge {
  display: inline-flex; align-items: center; padding: 0.2rem 0.7rem; border-radius: 999px; font-size: 0.8rem; font-weight: 700;
  &--cat { background: $cat-color; color: #fff; }
  &--gpr { background: linear-gradient(135deg, $gpr-mid, $gpr-color); color: #fff; }
}
.vpc-badge {
  display: inline-flex; align-items: center; padding: 0.2rem 0.6rem; border-radius: 4px; font-size: 0.8rem; font-weight: 600;
  &--success { background: #dcfce7; color: #166534; }
  &--warning { background: #fef9c3; color: #854d0e; }
  &--info    { background: #dbeafe; color: #1e40af; }
  &--edf-ok  { background: #d1fae5; color: #065f46; }
  &--edf-ko  { background: #fee2e2; color: #991b1b; }
}
.vpc-section {
  background: #fff; border-radius: 8px; box-shadow: 0 1px 3px rgba(0,0,0,.08);
  padding: 1.25rem; margin-bottom: 1.25rem; border-left: 4px solid #e2e8f0;
  &--cat { border-left-color: $cat-color; }
  &--gpr { border-left-color: $gpr-color; }
  &__title { display: flex; align-items: center; gap: 0.6rem; font-size: 1rem; font-weight: 700; color: #1e293b; margin-bottom: 1rem; }
  &__badge {
    display: inline-block; padding: 0.15rem 0.55rem; border-radius: 4px; font-size: 0.72rem; font-weight: 700; color: #fff;
    &--cat { background: $cat-color; }
    &--gpr { background: $gpr-color; }
  }
}
.vpc-summary-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 0.75rem; }
.vpc-summary-item {
  background: $gpr-light; border: 1px solid #bfdbfe; border-radius: 6px; padding: 0.6rem 0.8rem;
  &__label { display: block; font-size: 0.75rem; color: #64748b; }
  &__value { font-size: 1.05rem; font-weight: 700; color: $gpr-color; }
}
.vpc-lines { display: flex; flex-direction: column; gap: 0.6rem; }
.vpc-line-card {
  display: flex; gap: 0.75rem; align-items: flex-start; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 0.75rem;
  &--cat { border-left: 3px solid $cat-color; }
  &--gpr { border-left: 3px solid $gpr-color; }
  &__num {
    min-width: 1.6rem; height: 1.6rem; border-radius: 50%; background: $cat-light; color: $cat-dark;
    display: flex; align-items: center; justify-content: center; font-size: 0.78rem; font-weight: 700; flex-shrink: 0;
    &--gpr { background: $gpr-light; color: $gpr-color; }
  }
  &__body { flex: 1; }
  &__chips { display: flex; flex-wrap: wrap; gap: 0.35rem; }
  &__fields { display: flex; flex-wrap: wrap; gap: 0.3rem; margin-top: 0.4rem; }
  &__comment { margin-top: 0.4rem; font-size: 0.82rem; color: #64748b; font-style: italic; }
}
.vpc-chip {
  display: inline-flex; align-items: center; background: #fff; border: 1px solid #e2e8f0; border-radius: 4px; padding: 0.15rem 0.5rem; font-size: 0.8rem; color: #374151;
  &--room    { background: #f0f9ff; border-color: #bae6fd; color: #0369a1; }
  &--work    { background: #f0fdf4; border-color: #bbf7d0; color: #166534; }
  &--sinistre{ background: #fef9c3; border-color: #fde047; color: #854d0e; }
  &--edf-ok  { background: #d1fae5; border-color: #6ee7b7; color: #065f46; }
  &--edf-ko  { background: #fee2e2; border-color: #fca5a5; color: #991b1b; }
}
.vpc-field-pill {
  display: inline-flex; font-size: 0.75rem; border-radius: 4px; border: 1px solid #e2e8f0; overflow: hidden;
  &__key { background: #f1f5f9; color: #64748b; padding: 0.1rem 0.4rem; }
  &__val { background: #fff; color: #1e293b; padding: 0.1rem 0.4rem; font-weight: 600; }
}
.vpc-comment-block {
  background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 0.75rem; font-size: 0.9rem; color: #374151; line-height: 1.5;
  p { margin: 0.3rem 0 0; }
}
.vpc-readonly-banner {
  display: flex; align-items: center; background: #fffbeb; border: 1px solid #fde047; border-radius: 6px;
  padding: 0.65rem 1rem; font-size: 0.85rem; color: #78350f; margin-top: 1rem;
}
.spinner-small {
  width: 2rem; height: 2rem; border: 3px solid #e2e8f0; border-top-color: #6366f1; border-radius: 50%; animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
</style>
